import { useSearchParams } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { toast } from 'sonner';
import {
  Loader2, Plus, Trash2, ChevronUp, ChevronDown, ChevronLeft,
  Save, Pencil, Star, ClipboardList, Info, Eye, Type, AlignLeft,
  ToggleLeft, Hash, Image as ImageIcon, MapPin, Globe, RefreshCw, GripVertical, Settings2,
  Users, UserPlus, X, AlertTriangle, Search,
  CircleDot, List, ListChecks, PenLine, Heading2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const FREQ_OPTIONS = [
  { value: 'dagelijks', label: 'Dagelijks' },
  { value: 'wekelijks', label: 'Wekelijks' },
  { value: 'maandelijks', label: 'Maandelijks' },
  { value: 'kwartaal', label: 'Per kwartaal' },
  { value: 'jaarlijks', label: 'Jaarlijks' },
  { value: 'eenmalig', label: 'Eenmalig' },
  { value: 'aangepast', label: 'Aangepast interval' },
];

const ITEM_TYPES = [
  { value: 'checkbox', label: 'Checkbox (Ja/Nee)', icon: ToggleLeft },
  { value: 'tekst_kort', label: 'Kort tekstveld', icon: Type },
  { value: 'tekst_lang', label: 'Lang tekstveld', icon: AlignLeft },
  { value: 'beoordeling', label: 'Beoordeling', icon: Star },
  { value: 'foto', label: 'Foto upload', icon: ImageIcon },
  { value: 'nummer', label: 'Nummer', icon: Hash },
  { value: 'radio', label: 'Radio (één keuze)', icon: CircleDot },
  { value: 'keuzelijst', label: 'Keuzelijst', icon: List },
  { value: 'keuzelijst_meervoud', label: 'Keuzelijst meervoud', icon: ListChecks },
  { value: 'handtekening', label: 'Handtekening', icon: PenLine },
  { value: 'koptekst', label: 'Koptekst', icon: Heading2 },
];

const TYPES_WITH_OPTIES = new Set(['radio', 'keuzelijst', 'keuzelijst_meervoud']);
const TYPES_NIET_SCOREABLE = new Set([
  'tekst_kort', 'tekst_lang', 'beoordeling', 'foto', 'nummer',
  'keuzelijst', 'keuzelijst_meervoud', 'handtekening', 'koptekst',
]);

const ZICHTBAARHEID_OPTIONS = [
  { value: 'openbaar', label: 'Openbaar (iedereen)' },
  { value: 'eigen_locatie', label: 'Eigen locatie + HK' },
  { value: 'specifieke_rollen', label: 'Specifieke rollen' },
];

const ROLLEN = ['admin', 'manager', 'bedrijfsleider', 'medewerker'];

const DAGEN_OPTIES = [
  { value: 'geen', label: 'Niet ingesteld' },
  { value: 'maandag', label: 'Maandag' }, { value: 'dinsdag', label: 'Dinsdag' },
  { value: 'woensdag', label: 'Woensdag' }, { value: 'donderdag', label: 'Donderdag' },
  { value: 'vrijdag', label: 'Vrijdag' }, { value: 'zaterdag', label: 'Zaterdag' },
  { value: 'zondag', label: 'Zondag' },
];

const EMAIL_TRIGGERS = [
  { key: 'nieuwe_checklist', label: 'Nieuwe checklist klaarstaat', desc: 'Mail bij generatie', defaultRollen: ['bedrijfsleider', 'medewerker'] },
  { key: 'verlopen', label: 'Verlopen checklist', desc: 'Mail bij deadline overschrijding', defaultRollen: ['bedrijfsleider'] },
  { key: 'escalatie', label: 'MT-escalatie', desc: 'Mail naar management bij escalatie', defaultRollen: ['admin', 'manager'] },
  { key: 'ingevuld_notificatie', label: 'Checklist ingevuld', desc: 'Mail bij indienen', defaultRollen: ['admin', 'manager'] },
  { key: 'reminder', label: 'Herinnering', desc: 'Mail als checklist nog open staat voor de deadline', defaultRollen: ['bedrijfsleider', 'medewerker'], hasSchedule: true },
];

const EMAIL_NIEUW_DEFAULTS = {
  nieuwe_checklist: { actief: true, rollen: ['bedrijfsleider', 'medewerker'] },
  verlopen: { actief: true, rollen: ['bedrijfsleider'] },
  escalatie: { actief: true, rollen: ['admin', 'manager'] },
  ingevuld_notificatie: { actief: false, rollen: [] },
  reminder: { actief: false, rollen: ['bedrijfsleider', 'medewerker'], dag: 'zaterdag', tijd: '13:00' },
};

export const parseExtra = (tekst) => String(tekst || '')
  .split(/[,;\s]+/).map(x => x.trim().toLowerCase()).filter(Boolean)
  .filter((x, i, arr) => arr.indexOf(x) === i);

// De backend levert de genormaliseerde vorm (3.1). Alleen een gloednieuw template
// krijgt defaults; bestaande templates tonen EXACT wat er is opgeslagen.
export const normaliseerEmailInstellingen = (opgeslagen, isNieuw) => {
  const uit = {};
  EMAIL_TRIGGERS.forEach(({ key }) => {
    const bron = (opgeslagen && typeof opgeslagen[key] === 'object' && opgeslagen[key]) || null;
    const basis = isNieuw ? (EMAIL_NIEUW_DEFAULTS[key] || {}) : {};
    uit[key] = {
      actief: bron ? !!bron.actief : !!basis.actief,
      rollen: bron ? (bron.rollen || []) : (basis.rollen || []),
      extra_ontvangers: bron ? (bron.extra_ontvangers || []) : [],
      ...(key === 'reminder'
        ? { dag: (bron ? bron.dag : basis.dag) || '', tijd: (bron ? bron.tijd : basis.tijd) || '13:00' }
        : {}),
    };
  });
  return uit;
};

const ExtraOntvangers = ({ waarde, onOpslaan, testId }) => {
  const [tekst, setTekst] = React.useState((waarde || []).join(', '));
  React.useEffect(() => { setTekst((waarde || []).join(', ')); }, [waarde]);
  return (
    <Input
      value={tekst}
      onChange={e => setTekst(e.target.value)}
      onBlur={() => onOpslaan(tekst)}
      placeholder="joey@truckwash1group.nl, ..."
      className="h-7 text-xs mt-1"
      data-testid={testId}
    />
  );
};

// ── Helpers ────────────────────────────────────────────────────────────────

const moveInArray = (arr, idx, dir) => {
  const swap = idx + dir;
  if (swap < 0 || swap >= arr.length) return arr;
  const copy = [...arr];
  [copy[idx], copy[swap]] = [copy[swap], copy[idx]];
  return copy.map((item, i) => ({ ...item, volgorde: i + 1 }));
};

const newItem = (volgorde) => ({
  naam: '', beschrijving: '', type: 'checkbox',
  verplicht: true, foto_toegestaan: false, volgorde,
});

// Bij type-wijziging: zorg dat type-specifieke configs initieel bestaan.
const applyTypeDefaults = (item, newType) => {
  const base = { ...item, type: newType };
  if (TYPES_WITH_OPTIES.has(newType) && !Array.isArray(base.opties)) {
    base.opties = [{ key: 'optie_1', label: 'Optie 1' }, { key: 'optie_2', label: 'Optie 2' }];
  }
  if (newType === 'koptekst') {
    base.verplicht = false;
    base.foto_toegestaan = false;
  }
  return base;
};

const newInfoBlok = (volgorde, niveau = 'template') => ({
  titel: '', tekst: '', niveau, sectie_id: null, volgorde,
});

// ── Star Rating Preview ────────────────────────────────────────────────────

const StarRating = ({ count = 5, size = 16 }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} className="text-slate-300" style={{ width: size, height: size }} />
    ))}
  </div>
);

// ── Live Preview ───────────────────────────────────────────────────────────

const LivePreview = ({ form }) => {
  const schaal = form.beoordeling_schaal || 10;
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="live-preview">
      <div className="bg-[#1f2548] text-white px-4 py-3 flex items-center gap-2">
        <Eye className="w-4 h-4" />
        <span className="text-sm font-medium">Preview: {form.naam || 'Naamloos template'}</span>
      </div>
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {form.info_blokken?.map((ib, i) => (
          <div key={i} className="bg-slate-100 border border-slate-200 rounded-lg p-3" data-testid={`preview-info-${i}`}>
            <p className="font-semibold text-sm text-slate-600 mb-1">{ib.titel || 'Info'}</p>
            <p className="text-xs text-slate-500 whitespace-pre-wrap">{ib.tekst || '...'}</p>
          </div>
        ))}
        {form.secties?.map((sectie, si) => (
          <div key={si} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`preview-sectie-${si}`}>
            <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
              <h4 className="font-semibold text-sm text-[#1f2548]">{sectie.naam || `Sectie ${si + 1}`}</h4>
            </div>
            <div className="p-3 space-y-2">
              {sectie.info_blokken?.map((ib, ibi) => (
                <div key={ibi} className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                  <span className="font-semibold">{ib.titel || 'Info'}: </span>{ib.tekst || '...'}
                </div>
              ))}
              {sectie.items?.map((item, ii) => {
                if (item.type === 'koptekst') {
                  return (
                    <div key={ii} className="py-1.5 border-t border-slate-200 mt-1" data-testid={`preview-koptekst-${si}-${ii}`}>
                      <span className="font-semibold text-xs text-[#1f2548]">{item.naam || 'Koptekst'}</span>
                    </div>
                  );
                }
                return (
                <div key={ii} className="flex items-center gap-2 py-1">
                  {item.type === 'checkbox' && <div className="w-8 h-5 bg-slate-200 rounded-full flex-shrink-0" />}
                  {item.type === 'tekst_kort' && <div className="w-24 h-6 bg-slate-100 border border-slate-200 rounded flex-shrink-0" />}
                  {item.type === 'tekst_lang' && <div className="w-32 h-10 bg-slate-100 border border-slate-200 rounded flex-shrink-0" />}
                  {item.type === 'beoordeling' && <StarRating count={schaal} size={12} />}
                  {item.type === 'foto' && <div className="w-8 h-6 bg-slate-100 border border-dashed border-slate-300 rounded flex items-center justify-center flex-shrink-0"><ImageIcon className="w-3 h-3 text-slate-400" /></div>}
                  {item.type === 'nummer' && <div className="w-20 h-6 bg-slate-100 border border-slate-200 rounded flex items-center justify-end px-1.5 flex-shrink-0"><Hash className="w-3 h-3 text-slate-400" /></div>}
                  {item.type === 'radio' && (
                    <div className="flex gap-1 flex-shrink-0">
                      {(item.opties || []).slice(0, 3).map((o, oi) => (
                        <div key={oi} className="w-2.5 h-2.5 rounded-full border border-slate-300" />
                      ))}
                    </div>
                  )}
                  {item.type === 'keuzelijst' && <div className="w-20 h-6 bg-slate-100 border border-slate-200 rounded flex items-center px-1.5 flex-shrink-0"><ChevronDown className="w-3 h-3 text-slate-400" /></div>}
                  {item.type === 'keuzelijst_meervoud' && (
                    <div className="flex gap-1 flex-shrink-0">
                      {(item.opties || []).slice(0, 3).map((o, oi) => (
                        <div key={oi} className="w-2.5 h-2.5 border border-slate-300 rounded-sm" />
                      ))}
                    </div>
                  )}
                  {item.type === 'handtekening' && <div className="w-16 h-6 bg-slate-50 border border-dashed border-slate-300 rounded flex items-center justify-center flex-shrink-0"><PenLine className="w-3 h-3 text-slate-400" /></div>}
                  <span className="text-xs text-slate-600 truncate">{item.naam || '...'}</span>
                  {item.verplicht && <span className="text-red-400 text-xs">*</span>}
                  {item.foto_toegestaan && item.type !== 'foto' && <ImageIcon className="w-3 h-3 text-slate-400 flex-shrink-0" />}
                </div>
                );
              })}
              {form.sectie_velden_tonen && (
              <div className="border-t border-dashed border-slate-200 pt-2 mt-2 space-y-1">
                <div className="flex items-center gap-1 text-xs text-slate-400"><AlignLeft className="w-3 h-3" /> Toelichting</div>
                <div className="flex items-center gap-1 text-xs text-slate-400"><ImageIcon className="w-3 h-3" /> Foto's</div>
                <div className="flex items-center gap-1 text-xs text-slate-400"><ClipboardList className="w-3 h-3" /> Actiepunten</div>
              </div>
              )}
            </div>
          </div>
        ))}
        {form.items_zonder_sectie?.map((item, i) => {
          if (item.type === 'koptekst') {
            return (
              <div key={i} className="px-3 py-2 border-t border-slate-200" data-testid={`preview-los-koptekst-${i}`}>
                <span className="font-semibold text-sm text-[#1f2548]">{item.naam || 'Koptekst'}</span>
              </div>
            );
          }
          return (
          <div key={i} className="flex items-center gap-2 py-1 px-3 bg-slate-50 rounded-lg" data-testid={`preview-los-item-${i}`}>
            {item.type === 'checkbox' && <div className="w-8 h-5 bg-slate-200 rounded-full flex-shrink-0" />}
            {item.type === 'tekst_kort' && <div className="w-24 h-6 bg-slate-100 border rounded flex-shrink-0" />}
            {item.type === 'tekst_lang' && <div className="w-32 h-10 bg-slate-100 border rounded flex-shrink-0" />}
            {item.type === 'beoordeling' && <StarRating count={schaal} size={14} />}
            {item.type === 'foto' && <div className="w-10 h-8 bg-slate-100 border border-dashed border-slate-300 rounded flex items-center justify-center flex-shrink-0"><ImageIcon className="w-3.5 h-3.5 text-slate-400" /></div>}
            {item.type === 'nummer' && <div className="w-20 h-7 bg-slate-100 border rounded flex items-center justify-end px-2 flex-shrink-0"><Hash className="w-3.5 h-3.5 text-slate-400" /></div>}
            {item.type === 'radio' && (
              <div className="flex gap-1 flex-shrink-0">
                {(item.opties || []).slice(0, 3).map((o, oi) => (
                  <div key={oi} className="w-3 h-3 rounded-full border border-slate-300" />
                ))}
              </div>
            )}
            {item.type === 'keuzelijst' && <div className="w-20 h-7 bg-slate-100 border rounded flex items-center px-2 flex-shrink-0"><ChevronDown className="w-3.5 h-3.5 text-slate-400" /></div>}
            {item.type === 'keuzelijst_meervoud' && (
              <div className="flex gap-1 flex-shrink-0">
                {(item.opties || []).slice(0, 3).map((o, oi) => (
                  <div key={oi} className="w-3 h-3 border border-slate-300 rounded-sm" />
                ))}
              </div>
            )}
            {item.type === 'handtekening' && <div className="w-16 h-7 bg-slate-50 border border-dashed border-slate-300 rounded flex items-center justify-center flex-shrink-0"><PenLine className="w-3.5 h-3.5 text-slate-400" /></div>}
            <span className="text-sm text-slate-600">{item.naam || '...'}</span>
            {item.verplicht && <span className="text-red-400">*</span>}
          </div>
          );
        })}
        {!form.secties?.length && !form.items_zonder_sectie?.length && (
          <p className="text-slate-400 text-sm text-center py-8">Voeg secties of items toe om de preview te zien</p>
        )}
      </div>
    </div>
  );
};

// ── Item Editor Row ────────────────────────────────────────────────────────

const OptiesEditor = ({ opties = [], onChange, positieveKey, onPositieveChange, showPositief }) => {
  const updateOptie = (i, field, val) => {
    const next = opties.map((o, idx) => idx === i ? { ...o, [field]: val } : o);
    onChange(next);
  };
  const addOptie = () => {
    const nextKey = `optie_${opties.length + 1}`;
    onChange([...opties, { key: nextKey, label: `Optie ${opties.length + 1}` }]);
  };
  const removeOptie = (i) => {
    const removed = opties[i];
    onChange(opties.filter((_, idx) => idx !== i));
    // Reset positieveKey als die optie weg is
    if (removed && removed.key === positieveKey && onPositieveChange) {
      onPositieveChange(null);
    }
  };
  return (
    <div className="mt-1.5 pl-2 border-l-2 border-slate-200 space-y-1" data-testid="opties-editor">
      <div className="flex items-center justify-between text-xs text-slate-600">
        <span className="font-medium">Opties</span>
        <button
          type="button"
          onClick={addOptie}
          className="text-xs text-[#1f2548] hover:text-[#1f2548]/70 flex items-center gap-1"
          data-testid="add-optie-btn"
        >
          <Plus className="w-3 h-3" /> Optie
        </button>
      </div>
      {opties.length === 0 && (
        <p className="text-xs text-slate-400 italic">Nog geen opties — klik op "Optie"</p>
      )}
      {opties.map((o, i) => (
        <div key={i} className="flex items-center gap-1.5" data-testid={`optie-row-${i}`}>
          {showPositief && onPositieveChange && (
            <label className="flex items-center" title="Markeer als positieve optie (scoreable)">
              <input
                type="radio"
                checked={positieveKey === o.key}
                onChange={() => onPositieveChange(positieveKey === o.key ? null : o.key)}
                className="w-3.5 h-3.5 accent-green-600"
                data-testid={`optie-positief-${i}`}
              />
            </label>
          )}
          <Input
            value={o.label || ''}
            onChange={e => updateOptie(i, 'label', e.target.value)}
            placeholder="Label"
            className="h-7 text-xs flex-1"
            data-testid={`optie-label-${i}`}
          />
          <Input
            value={o.key || ''}
            onChange={e => updateOptie(i, 'key', e.target.value.replace(/[^a-z0-9_]/gi, '_').toLowerCase())}
            placeholder="key"
            className="h-7 text-xs w-20 font-mono"
            data-testid={`optie-key-${i}`}
          />
          <button
            type="button"
            onClick={() => removeOptie(i)}
            className="p-1 text-red-400 hover:text-red-600"
            data-testid={`optie-delete-${i}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
      {showPositief && (
        <p className="text-[10px] text-slate-400 pl-0.5">
          Positieve optie (groen rondje) telt als "goed" in score. Leeg = item telt niet mee.
        </p>
      )}
    </div>
  );
};

const ItemRow = ({ item, index, total, onUpdate, onMerge, onDelete, onMove }) => {
  const t = item.type;
  const showOpties = TYPES_WITH_OPTIES.has(t);
  const showNummerConfig = t === 'nummer';
  const showTekstKortConfig = t === 'tekst_kort';
  const isKoptekst = t === 'koptekst';
  const isDisplayOnly = isKoptekst;
  return (
    <div className="flex items-start gap-2 bg-white border border-slate-200 rounded-lg p-2" data-testid={`item-row-${index}`}>
      <div className="flex flex-col gap-0.5 pt-1">
        <button onClick={() => onMove(-1)} disabled={index === 0} className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
        <button onClick={() => onMove(1)} disabled={index === total - 1} className="p-0.5 hover:bg-slate-100 rounded disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
      </div>
      <div className="flex-1 space-y-1.5">
        <Input
          value={item.naam}
          onChange={e => onUpdate('naam', e.target.value)}
          placeholder={isKoptekst ? "Koptekst / instructie" : "Item naam / vraag"}
          className="h-8 text-sm"
          data-testid={`item-naam-${index}`}
        />
        {!isKoptekst && (
          <Input
            value={item.beschrijving || ''}
            onChange={e => onUpdate('beschrijving', e.target.value)}
            placeholder="Beschrijving (optioneel)"
            className="h-7 text-xs"
          />
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <Select
            value={item.type}
            onValueChange={v => {
              const updated = applyTypeDefaults(item, v);
              // Atomische merge voorkomt stale-state race tussen meerdere sequentiële onUpdate-calls
              const patch = {};
              Object.keys(updated).forEach(k => {
                if (updated[k] !== item[k]) patch[k] = updated[k];
              });
              if (onMerge) {
                onMerge(patch);
              } else {
                Object.entries(patch).forEach(([k, v]) => onUpdate(k, v));
              }
            }}
          >
            <SelectTrigger className="h-7 w-48 text-xs" data-testid={`item-type-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {ITEM_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <SelectItem key={type.value} value={type.value}>
                    <span className="flex items-center gap-1.5">
                      <Icon className="w-3 h-3" />
                      {type.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {!isDisplayOnly && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Switch checked={item.verplicht} onCheckedChange={v => onUpdate('verplicht', v)} className="scale-75" />
              Verplicht
            </label>
          )}
          {!isDisplayOnly && t !== 'handtekening' && (
            <label className="flex items-center gap-1.5 text-xs cursor-pointer">
              <Switch checked={item.foto_toegestaan} onCheckedChange={v => onUpdate('foto_toegestaan', v)} className="scale-75" />
              Foto
            </label>
          )}
          {TYPES_NIET_SCOREABLE.has(t) && t !== 'koptekst' && (
            <span className="text-[10px] text-slate-400 italic">Niet scoreable</span>
          )}
          {t === 'radio' && !item.positieve_optie_key && (
            <span className="text-[10px] text-amber-600 italic">Kies positieve optie voor scoring</span>
          )}
        </div>
        {showOpties && (
          <OptiesEditor
            opties={item.opties || []}
            onChange={opties => onUpdate('opties', opties)}
            positieveKey={item.positieve_optie_key || null}
            onPositieveChange={t === 'radio' ? (key => onUpdate('positieve_optie_key', key)) : null}
            showPositief={t === 'radio'}
          />
        )}
        {showNummerConfig && (
          <div className="grid grid-cols-3 gap-1.5 mt-1.5" data-testid="nummer-config">
            <Input
              type="number"
              value={item.min_waarde ?? ''}
              onChange={e => onUpdate('min_waarde', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="Min"
              className="h-7 text-xs"
              data-testid="nummer-min"
            />
            <Input
              type="number"
              value={item.max_waarde ?? ''}
              onChange={e => onUpdate('max_waarde', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="Max"
              className="h-7 text-xs"
              data-testid="nummer-max"
            />
            <Input
              value={item.unit ?? ''}
              onChange={e => onUpdate('unit', e.target.value || null)}
              placeholder="Eenheid (bijv. km)"
              className="h-7 text-xs"
              data-testid="nummer-unit"
            />
          </div>
        )}
        {showTekstKortConfig && (
          <div className="grid grid-cols-2 gap-1.5 mt-1.5" data-testid="tekst-kort-config">
            <Input
              type="number"
              value={item.min_lengte ?? ''}
              onChange={e => onUpdate('min_lengte', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="Min. tekens"
              className="h-7 text-xs"
              data-testid="tekst-min"
            />
            <Input
              type="number"
              value={item.max_lengte ?? ''}
              onChange={e => onUpdate('max_lengte', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="Max. tekens"
              className="h-7 text-xs"
              data-testid="tekst-max"
            />
          </div>
        )}
      </div>
      <button onClick={onDelete} className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600 mt-1" data-testid={`item-delete-${index}`}>
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

// ── Genereer Modal (Interstitial) ──────────────────────────────────────────

const GenereerModal = ({ open, onClose, templateId, templateNaam }) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [matchingUsers, setMatchingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [matchingLocaties, setMatchingLocaties] = useState([]);
  const [allLocaties, setAllLocaties] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedLocIds, setSelectedLocIds] = useState(new Set());
  const [modus, setModus] = useState('locatie');
  const [search, setSearch] = useState('');
  const [addingUser, setAddingUser] = useState('');
  const [addingLoc, setAddingLoc] = useState('');

  useEffect(() => {
    if (!open || !templateId) return;
    setSearch('');
    setLoading(true);
    fetch(`${API_V2}/checklist-templates/${templateId}/matching-users`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) {
          const matching = d.matching || [];
          setMatchingUsers(matching);
          setAllUsers(d.all_users || []);
          setMatchingLocaties(d.matching_locaties || []);
          setAllLocaties(d.all_locaties || []);
          setModus(d.modus || 'locatie');
          // Pre-select: users WITHOUT open checklist are ON, WITH open checklist are OFF
          setSelectedIds(new Set(matching.filter(u => !u.heeft_open_checklist).map(u => u.id)));
          // Pre-select: locations WITHOUT open checklist are ON, WITH are OFF
          const locs = d.matching_locaties || [];
          setSelectedLocIds(new Set(locs.filter(l => !l.heeft_open_checklist).map(l => l.id)));
        }
      })
      .catch(() => toast.error('Gebruikers laden mislukt'))
      .finally(() => setLoading(false));
  }, [open, templateId]);

  const toggleUser = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleLoc = (id) => {
    setSelectedLocIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filtered = matchingUsers.filter(u =>
    !search || u.naam?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const filteredLocs = matchingLocaties.filter(l =>
    !search || l.naam?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleAll = () => {
    if (modus === 'locatie') {
      const fIds = filteredLocs.map(l => l.id);
      const allSel = fIds.every(id => selectedLocIds.has(id));
      setSelectedLocIds(prev => {
        const next = new Set(prev);
        if (allSel) fIds.forEach(id => next.delete(id));
        else fIds.forEach(id => next.add(id));
        return next;
      });
    } else {
      const fIds = filtered.map(u => u.id);
      const allSel = fIds.every(id => selectedIds.has(id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (allSel) fIds.forEach(id => next.delete(id));
        else fIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const addUser = (userId) => {
    if (!userId) return;
    const u = allUsers.find(x => x.id === userId);
    if (u && !matchingUsers.find(m => m.id === userId)) {
      setMatchingUsers(prev => [...prev, { ...u, heeft_open_checklist: false }]);
    }
    setSelectedIds(prev => new Set([...prev, userId]));
    setAddingUser('');
  };

  const addLoc = (locId) => {
    if (!locId) return;
    const l = allLocaties.find(x => x.id === locId);
    if (l && !matchingLocaties.find(m => m.id === locId)) {
      setMatchingLocaties(prev => [...prev, { ...l, heeft_open_checklist: false }]);
    }
    setSelectedLocIds(prev => new Set([...prev, locId]));
    setAddingLoc('');
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const body = modus === 'persoon'
        ? { user_ids: [...selectedIds] }
        : { locatie_ids: [...selectedLocIds] };
      const res = await fetch(`${API_V2}/checklist-templates/${templateId}/genereer`, {
        method: 'POST', headers: hdrs(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message || 'Checklists gegenereerd'); onClose(); }
      else toast.error(data.detail || 'Genereren mislukt');
    } catch { toast.error('Genereren mislukt'); }
    finally { setGenerating(false); }
  };

  const availableToAdd = allUsers.filter(u => !matchingUsers.find(m => m.id === u.id));
  const availableLocsToAdd = allLocaties.filter(l => !matchingLocaties.find(m => m.id === l.id));
  const selCount = modus === 'persoon' ? selectedIds.size : selectedLocIds.size;
  const totalCount = modus === 'persoon' ? matchingUsers.length : matchingLocaties.length;
  const isAllSelected = modus === 'persoon'
    ? filtered.length > 0 && filtered.every(u => selectedIds.has(u.id))
    : filteredLocs.length > 0 && filteredLocs.every(l => selectedLocIds.has(l.id));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle data-testid="genereer-modal-title">
            Checklists aanmaken voor {templateNaam}
          </DialogTitle>
          <DialogDescription>
            {modus === 'persoon' ? 'Per persoon — selecteer wie een checklist krijgt' : 'Locatie-breed — selecteer locaties'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#1f2548]" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col space-y-3 py-2">
            {/* Search bar */}
            <Input placeholder={modus === 'persoon' ? 'Zoek op naam...' : 'Zoek locatie...'} value={search} onChange={e => setSearch(e.target.value)}
              className="h-8 text-sm" data-testid="user-search-input" />

            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                {selCount} van {totalCount} geselecteerd
              </p>
              <button onClick={toggleAll} className="text-xs text-blue-600 hover:underline" data-testid="toggle-all-users">
                {isAllSelected ? 'Deselecteer alles' : 'Selecteer alles'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-[40vh]" data-testid="matching-users-list">
              {modus === 'persoon' ? (
                /* ── Persoon modus ── */
                <>
                  {filtered.map(u => (
                    <label key={u.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${u.heeft_open_checklist ? 'bg-amber-50/50' : 'hover:bg-slate-50'}`} data-testid={`user-row-${u.id}`}>
                      <Checkbox checked={selectedIds.has(u.id)} onCheckedChange={() => toggleUser(u.id)} />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-700 block truncate">{u.naam}</span>
                        {u.heeft_open_checklist && (
                          <span className="text-[11px] text-amber-600 flex items-center gap-0.5">
                            <AlertTriangle className="w-3 h-3" /> Heeft al een open checklist
                          </span>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <Badge variant="outline" className="text-[10px]">{u.rol}</Badge>
                        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-0.5 justify-end">
                          <MapPin className="w-2.5 h-2.5" />{u.thuislocatie_naam || '-'}
                        </p>
                      </div>
                    </label>
                  ))}
                  {filtered.length === 0 && (
                    <div className="py-6 text-center text-slate-400 text-sm">{search ? 'Geen resultaten' : 'Geen matching gebruikers'}</div>
                  )}
                </>
              ) : (
                /* ── Locatie modus ── */
                <>
                  {filteredLocs.map(l => (
                    <label key={l.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${l.heeft_open_checklist ? 'bg-amber-50/50' : 'hover:bg-slate-50'}`} data-testid={`loc-row-${l.id}`}>
                      <Checkbox checked={selectedLocIds.has(l.id)} onCheckedChange={() => toggleLoc(l.id)} />
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-700">{l.naam}</span>
                      </div>
                      {l.heeft_open_checklist && (
                        <span className="text-[11px] text-amber-600 flex items-center gap-0.5 flex-shrink-0">
                          <AlertTriangle className="w-3 h-3" /> Open checklist
                        </span>
                      )}
                    </label>
                  ))}
                  {filteredLocs.length === 0 && (
                    <div className="py-6 text-center text-slate-400 text-sm">{search ? 'Geen resultaten' : 'Geen matching locaties'}</div>
                  )}
                </>
              )}
            </div>

            {/* Add extra user / location */}
            {modus === 'persoon' && availableToAdd.length > 0 && (
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <Select value={addingUser} onValueChange={addUser}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="add-user-dropdown">
                    <SelectValue placeholder="Persoon toevoegen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableToAdd.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.naam} — {u.thuislocatie_naam || 'Geen locatie'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {modus === 'locatie' && availableLocsToAdd.length > 0 && (
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <Select value={addingLoc} onValueChange={addLoc}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="add-loc-dropdown">
                    <SelectValue placeholder="Locatie toevoegen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLocsToAdd.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.naam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleGenerate} disabled={generating || selCount === 0}
            className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="confirm-generate-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Genereer voor {selCount} {modus === 'persoon' ? (selCount === 1 ? 'persoon' : 'personen') : (selCount === 1 ? 'locatie' : 'locaties')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Soft Delete Modal ──────────────────────────────────────────────────────

const SoftDeleteModal = ({ open, onClose, template, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!template?.id) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_V2}/checklist-templates/${template.id}/permanent`, {
        method: 'DELETE', headers: hdrs(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Template verwijderd');
        onDeleted?.();
        onClose();
      } else toast.error(data.detail || 'Verwijderen mislukt');
    } catch { toast.error('Verwijderen mislukt'); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-red-600">Template verwijderen?</DialogTitle>
          <DialogDescription>
            Template "{template?.naam}" wordt permanent verwijderd. Alle bijbehorende checklists (open én ingevuld) worden ook verwijderd. Dit kan niet ongedaan worden.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-soft-delete-btn">
            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
            Permanent verwijderen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ── Template Editor ────────────────────────────────────────────────────────

const TemplateEditor = ({ template, onBack, onSaved, locaties, onGenerate, onSoftDelete }) => {
  const isNew = !template?.id;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => ({
    naam: template?.naam || '',
    beschrijving: template?.beschrijving || '',
    type: template?.type || 'locatie',
    actief: template?.actief ?? true,
    gepauzeerd: template?.gepauzeerd ?? (template ? false : true),
    frequentie: template?.frequentie || { type: 'wekelijks', interval_dagen: null, deadline_dagen: 2, escalatie_dagen: 3, deadline_dag: null, deadline_tijd: '23:59' },
    toewijzing: template?.toewijzing || { alle_locaties: true, locatie_ids: [] },
    doelgroep: template?.doelgroep || { rollen: [], gebruiker_ids: [] },
    invulling_modus: template?.invulling_modus || 'locatie',
    zichtbaarheid: template?.zichtbaarheid || { niveau: 'openbaar', toegestane_rollen: [] },
    beoordeling_schaal: template?.beoordeling_schaal || 10,
    score_berekening: template?.score_berekening ?? true,
    sectie_velden_tonen: template?.sectie_velden_tonen ?? (template?.type === 'persoonlijk' ? false : true),
    info_blokken: template?.info_blokken || [],
    secties: template?.secties?.map(s => ({ ...s, items: s.items?.map(i => ({ ...i })) || [], info_blokken: s.info_blokken?.map(ib => ({ ...ib })) || [] })) || [],
    items_zonder_sectie: template?.items_zonder_sectie?.map(i => ({ ...i })) || [],
    email_instellingen: normaliseerEmailInstellingen(template?.email_instellingen, !template?.id),
  }));

  const setField = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const setFreq = (key, val) => setForm(p => ({ ...p, frequentie: { ...p.frequentie, [key]: val } }));
  const [gebruikers, setGebruikers] = useState([]);
  const [doelgroepZoek, setDoelgroepZoek] = useState('');

  useEffect(() => {
    if (form.invulling_modus !== 'persoon' || gebruikers.length > 0) return;
    fetch(`${API}/gebruikers`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setGebruikers((Array.isArray(d) ? d : d?.items || [])
        .filter(u => u.actief !== false)
        .map(u => ({ id: u.id || u._id, naam: u.naam, email: u.email, rol: u.rol }))))
      .catch(() => setGebruikers([]));
  }, [form.invulling_modus, gebruikers.length]);

  const setToewijzing = (key, val) => setForm(p => ({ ...p, toewijzing: { ...p.toewijzing, [key]: val } }));
  const setDoelgroep = (key, val) => setForm(p => ({ ...p, doelgroep: { ...(p.doelgroep || {}), [key]: val } }));
  const setZichtbaarheid = (key, val) => setForm(p => ({ ...p, zichtbaarheid: { ...p.zichtbaarheid, [key]: val } }));
  const setEmailInst = (trigger, field, val) => setForm(p => ({
    ...p, email_instellingen: {
      ...p.email_instellingen,
      [trigger]: { ...(p.email_instellingen?.[trigger] || {}), [field]: val },
    },
  }));
  // 6D.1: herinnering aanzetten vult meteen de standaard dag/tijd in de state,
  // zodat wat je ziet ook echt wordt opgeslagen.
  const setEmailActief = (trigger, val) => setForm(p => {
    const huidig = p.email_instellingen?.[trigger] || {};
    const extra = (val && trigger === 'reminder')
      ? { dag: huidig.dag || 'zaterdag', tijd: huidig.tijd || '13:00' }
      : {};
    return {
      ...p, email_instellingen: {
        ...p.email_instellingen,
        [trigger]: { ...huidig, ...extra, actief: val },
      },
    };
  });
  const toggleEmailRol = (trigger, rol) => setForm(p => {
    const curr = p.email_instellingen?.[trigger]?.rollen || [];
    const rollen = curr.includes(rol) ? curr.filter(r => r !== rol) : [...curr, rol];
    return { ...p, email_instellingen: { ...p.email_instellingen, [trigger]: { ...(p.email_instellingen?.[trigger] || {}), rollen } } };
  });
  const setEmailExtra = (trigger, val) => setForm(p => ({
    ...p, email_instellingen: {
      ...p.email_instellingen,
      [trigger]: { ...(p.email_instellingen?.[trigger] || {}), extra_ontvangers: parseExtra(val) },
    },
  }));

  const toggleLocatie = (locId) => {
    setForm(p => {
      const ids = p.toewijzing.locatie_ids || [];
      return { ...p, toewijzing: { ...p.toewijzing, locatie_ids: ids.includes(locId) ? ids.filter(x => x !== locId) : [...ids, locId] } };
    });
  };

  const toggleRol = (rol) => {
    setForm(p => {
      const rollen = p.zichtbaarheid.toegestane_rollen || [];
      return { ...p, zichtbaarheid: { ...p.zichtbaarheid, toegestane_rollen: rollen.includes(rol) ? rollen.filter(x => x !== rol) : [...rollen, rol] } };
    });
  };

  // ── Info Blokken ──
  const addInfoBlok = () => setField('info_blokken', [...form.info_blokken, newInfoBlok(form.info_blokken.length + 1)]);
  const updateInfoBlok = (i, key, val) => setField('info_blokken', form.info_blokken.map((ib, j) => j === i ? { ...ib, [key]: val } : ib));
  const removeInfoBlok = (i) => setField('info_blokken', form.info_blokken.filter((_, j) => j !== i));
  const moveInfoBlok = (i, dir) => setField('info_blokken', moveInArray(form.info_blokken, i, dir));

  // ── Secties ──
  const addSectie = () => setField('secties', [...form.secties, { naam: '', volgorde: form.secties.length + 1, items: [], info_blokken: [] }]);
  const removeSectie = (i) => setField('secties', form.secties.filter((_, j) => j !== i));
  const moveSectie = (i, dir) => setField('secties', moveInArray(form.secties, i, dir));
  const updateSectie = (i, key, val) => setField('secties', form.secties.map((s, j) => j === i ? { ...s, [key]: val } : s));

  const addItemToSectie = (si) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], items: [...secties[si].items, newItem(secties[si].items.length + 1)] };
    setField('secties', secties);
  };
  const removeItemFromSectie = (si, ii) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], items: secties[si].items.filter((_, j) => j !== ii).map((it, j) => ({ ...it, volgorde: j + 1 })) };
    setField('secties', secties);
  };
  const updateItemInSectie = (si, ii, key, val) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], items: secties[si].items.map((it, j) => j === ii ? { ...it, [key]: val } : it) };
    setField('secties', secties);
  };
  // Merge-variant: applyt multiple keys atomisch (voorkomt stale-state races
  // bij type-switching waar meerdere velden tegelijk moeten updaten).
  const mergeItemInSectie = (si, ii, patch) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], items: secties[si].items.map((it, j) => j === ii ? { ...it, ...patch } : it) };
    setField('secties', secties);
  };
  const moveItemInSectie = (si, ii, dir) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], items: moveInArray(secties[si].items, ii, dir) };
    setField('secties', secties);
  };

  // ── Sectie Info Blokken ──
  const addSectieInfoBlok = (si) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], info_blokken: [...(secties[si].info_blokken || []), newInfoBlok((secties[si].info_blokken || []).length + 1, 'sectie')] };
    setField('secties', secties);
  };
  const updateSectieInfoBlok = (si, ibi, key, val) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], info_blokken: secties[si].info_blokken.map((ib, j) => j === ibi ? { ...ib, [key]: val } : ib) };
    setField('secties', secties);
  };
  const removeSectieInfoBlok = (si, ibi) => {
    const secties = [...form.secties];
    secties[si] = { ...secties[si], info_blokken: secties[si].info_blokken.filter((_, j) => j !== ibi) };
    setField('secties', secties);
  };

  // ── Items zonder sectie ──
  const addLooseItem = () => setField('items_zonder_sectie', [...form.items_zonder_sectie, newItem(form.items_zonder_sectie.length + 1)]);
  const removeLooseItem = (i) => setField('items_zonder_sectie', form.items_zonder_sectie.filter((_, j) => j !== i).map((it, j) => ({ ...it, volgorde: j + 1 })));
  const updateLooseItem = (i, key, val) => setField('items_zonder_sectie', form.items_zonder_sectie.map((it, j) => j === i ? { ...it, [key]: val } : it));
  const mergeLooseItem = (i, patch) => setField('items_zonder_sectie', form.items_zonder_sectie.map((it, j) => j === i ? { ...it, ...patch } : it));
  const moveLooseItem = (i, dir) => setField('items_zonder_sectie', moveInArray(form.items_zonder_sectie, i, dir));

  const handleSave = async () => {
    if (!form.naam.trim()) { toast.error('Naam is verplicht'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API_V2}/checklist-templates` : `${API_V2}/checklist-templates/${template.id}`;
      const payload = isNew ? form : { ...form, updated_at: template?.updated_at };
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: hdrs(), body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(isNew ? 'Template aangemaakt' : 'Template bijgewerkt');
        onSaved?.();
        onBack();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || (res.status === 409
          ? 'Dit template is intussen gewijzigd. Herlaad de pagina.'
          : 'Opslaan mislukt'), { duration: res.status === 409 ? 8000 : 4000 });
      }
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  const totalItems = form.secties.reduce((s, sec) => s + sec.items.length, 0) + form.items_zonder_sectie.length;

  return (
    <div className="space-y-4" data-testid="template-editor">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-1 text-slate-600" data-testid="editor-back-btn">
          <ChevronLeft className="w-4 h-4" /> Terug
        </Button>
        <div className="flex items-center gap-3">
          {!isNew && (
            <div className="flex items-center gap-1.5">
              <Switch checked={form.actief !== false} onCheckedChange={v => setField('actief', v)} data-testid="editor-actief-toggle" />
              <span className="text-xs text-slate-500">{form.actief !== false ? 'Actief' : 'Inactief'}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Switch checked={!form.gepauzeerd} onCheckedChange={v => setField('gepauzeerd', !v)} data-testid="editor-pauze-toggle" />
            <span className={`text-xs ${form.gepauzeerd ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>{form.gepauzeerd ? 'Gepauzeerd' : 'Live'}</span>
          </div>
          {!isNew && (
            <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700"
              onClick={() => onSoftDelete?.(template)} data-testid="editor-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Verwijderen
            </Button>
          )}
          {!isNew && (
            <Button variant="outline" size="sm" onClick={onGenerate} data-testid="generate-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Nu genereren
            </Button>
          )}
          <span className="text-xs text-slate-500">{form.secties.length} secties, {totalItems} items</span>
          <Button onClick={handleSave} disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="save-template-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Opslaan
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Left: Form ── */}
        <div className="lg:col-span-3 space-y-5">
          {/* ── Sectie: Basis ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="basis-instellingen">
            <h3 className="font-semibold text-[#1f2548]">Basis</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Naam *</Label><Input value={form.naam} onChange={e => setField('naam', e.target.value)} placeholder="Template naam" data-testid="template-naam-input" /></div>
              <div><Label>Type</Label>
                <Select value={form.type} onValueChange={v => { setField('type', v); if (v === 'persoonlijk') { setField('score_berekening', false); setField('sectie_velden_tonen', false); } if (v === 'locatie') { setField('score_berekening', true); setField('sectie_velden_tonen', true); } }}>
                  <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="locatie">Locatie</SelectItem>
                    <SelectItem value="persoonlijk">Persoonlijk</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Beschrijving</Label><Textarea value={form.beschrijving} onChange={e => setField('beschrijving', e.target.value)} placeholder="Optionele beschrijving" rows={2} /></div>
          </div>

          {/* ── Sectie: Planning ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="planning-instellingen">
            <h3 className="font-semibold text-[#1f2548]">Planning</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Frequentie</Label>
                <Select value={form.frequentie.type} onValueChange={v => setFreq('type', v)}>
                  <SelectTrigger className="text-sm" data-testid="freq-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.frequentie.type === 'aangepast' && (
                <div><Label>Interval (dagen)</Label><Input type="number" value={form.frequentie.interval_dagen || ''} onChange={e => setFreq('interval_dagen', parseInt(e.target.value) || null)} min={1} /></div>
              )}
              <div><Label>Deadline (dagen)</Label><Input type="number" value={form.frequentie.deadline_dagen || ''} onChange={e => setFreq('deadline_dagen', parseInt(e.target.value) || 0)} min={0} data-testid="deadline-dagen-input" /></div>
              <div><Label>Escalatie na (dagen) — leeg = geen escalatie</Label>
                <Input type="number" value={form.frequentie.escalatie_dagen ?? ''}
                  onChange={e => setFreq('escalatie_dagen', e.target.value === '' ? null : (parseInt(e.target.value) || 0))}
                  min={0} data-testid="escalatie-dagen-input" /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Deadline dag</Label>
                <Select value={form.frequentie.deadline_dag || 'geen'} onValueChange={v => setFreq('deadline_dag', v === 'geen' ? null : v)}>
                  <SelectTrigger className="text-sm" data-testid="deadline-dag-select"><SelectValue placeholder="Niet ingesteld" /></SelectTrigger>
                  <SelectContent>{DAGEN_OPTIES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Deadline tijd</Label>
                <Input type="time" value={form.frequentie.deadline_tijd || '23:59'} onChange={e => setFreq('deadline_tijd', e.target.value)} data-testid="deadline-tijd-input" />
              </div>
            </div>
          </div>

          {/* ── Sectie: Toewijzing ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="toewijzing-instellingen">
            <h3 className="font-semibold text-[#1f2548]">Toewijzing</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Locatie-toewijzing</Label>
                <Select value={form.toewijzing.alle_locaties ? 'alle' : 'specifiek'} onValueChange={v => setToewijzing('alle_locaties', v === 'alle')} data-testid="locatie-toewijzing-select">
                  <SelectTrigger className="mb-2" data-testid="alle-locaties-dropdown">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle locaties</SelectItem>
                    <SelectItem value="specifiek">Specifieke locaties selecteren</SelectItem>
                  </SelectContent>
                </Select>
                {!form.toewijzing.alle_locaties && (
                  <div className="max-h-32 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                    {locaties.map(loc => (
                      <label key={loc._id || loc.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-slate-50 px-1 rounded">
                        <Checkbox checked={(form.toewijzing.locatie_ids || []).includes(loc._id || loc.id)} onCheckedChange={() => toggleLocatie(loc._id || loc.id)} />
                        {loc.naam}
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div><Label>Invullingsmodus</Label>
                <Select value={form.invulling_modus} onValueChange={v => setField('invulling_modus', v)}>
                  <SelectTrigger data-testid="invulling-modus-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="locatie">Locatie-breed</SelectItem>
                    <SelectItem value="persoon">Per persoon</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.invulling_modus === 'persoon' && (
              <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="doelgroep-sectie">
                <div>
                  <Label>Voor wie</Label>
                  <p className="text-xs text-slate-400">Leeg = iedereen met deze thuislocatie</p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {ROLLEN.map(rol => (
                    <label key={rol} className="flex items-center gap-2 text-sm cursor-pointer capitalize">
                      <Checkbox
                        checked={(form.doelgroep?.rollen || []).includes(rol)}
                        onCheckedChange={c => setDoelgroep('rollen', c
                          ? [...(form.doelgroep?.rollen || []), rol]
                          : (form.doelgroep?.rollen || []).filter(r => r !== rol))}
                        data-testid={`doelgroep-rol-${rol}`}
                      />
                      {rol}
                    </label>
                  ))}
                </div>
                <div>
                  <Input placeholder="Zoek een persoon…" value={doelgroepZoek}
                    onChange={e => setDoelgroepZoek(e.target.value)} data-testid="doelgroep-zoek" />
                  <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                    {(gebruikers || [])
                      .filter(u => !doelgroepZoek || (u.naam || '').toLowerCase().includes(doelgroepZoek.toLowerCase())
                        || (u.email || '').toLowerCase().includes(doelgroepZoek.toLowerCase()))
                      .slice(0, 50)
                      .map(u => (
                        <label key={u.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                          <Checkbox
                            checked={(form.doelgroep?.gebruiker_ids || []).includes(u.id)}
                            onCheckedChange={c => setDoelgroep('gebruiker_ids', c
                              ? [...(form.doelgroep?.gebruiker_ids || []), u.id]
                              : (form.doelgroep?.gebruiker_ids || []).filter(g => g !== u.id))}
                            data-testid={`doelgroep-gebruiker-${u.id}`}
                          />
                          <span className="flex-1 truncate">{u.naam || u.email}</span>
                          <span className="text-xs text-slate-400">{u.rol}</span>
                        </label>
                      ))}
                    {(gebruikers || []).length === 0 && (
                      <p className="px-3 py-2 text-xs text-slate-400">Geen gebruikers gevonden</p>
                    )}
                  </div>
                  {(form.doelgroep?.gebruiker_ids || []).length > 0 && (
                    <p className="text-xs text-slate-500 mt-1" data-testid="doelgroep-geselecteerd">
                      {form.doelgroep.gebruiker_ids.length} {form.doelgroep.gebruiker_ids.length === 1 ? 'persoon' : 'personen'} geselecteerd
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Sectie: Weergave ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="weergave-instellingen">
            <h3 className="font-semibold text-[#1f2548]">Weergave</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Beoordelingsschaal</Label>
                <Select value={String(form.beoordeling_schaal)} onValueChange={v => setField('beoordeling_schaal', parseInt(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">1 - 5</SelectItem>
                    <SelectItem value="10">1 - 10</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={form.score_berekening} onCheckedChange={v => setField('score_berekening', v)} data-testid="score-toggle" />
                  <span className="text-sm">Score berekening</span>
                </label>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch checked={form.sectie_velden_tonen} onCheckedChange={v => setField('sectie_velden_tonen', v)} data-testid="sectie-velden-toggle" />
                  <span className="text-sm">Sectie-velden</span>
                </label>
              </div>
            </div>
            <p className="text-xs text-slate-400">Sectie-velden: toelichting, foto's en actiepunten per sectie in het invulformulier</p>
          </div>

          {/* ── Sectie: Zichtbaarheid ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="zichtbaarheid-instellingen">
            <h3 className="font-semibold text-[#1f2548]">Zichtbaarheid</h3>
            <Select value={form.zichtbaarheid.niveau} onValueChange={v => setZichtbaarheid('niveau', v)}>
              <SelectTrigger data-testid="zichtbaarheid-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ZICHTBAARHEID_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            {form.zichtbaarheid.niveau === 'specifieke_rollen' && (
              <div className="flex flex-wrap gap-2">
                {ROLLEN.map(rol => (
                  <label key={rol} className="flex items-center gap-1.5 text-sm cursor-pointer bg-slate-50 px-2 py-1 rounded-lg border">
                    <Checkbox checked={(form.zichtbaarheid.toegestane_rollen || []).includes(rol)} onCheckedChange={() => toggleRol(rol)} />
                    {rol}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* ── Sectie: E-mail notificaties ── */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4" data-testid="email-instellingen-section">
            <h3 className="font-semibold text-[#1f2548] flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              E-mail notificaties
            </h3>
            <div className="space-y-3">
              {EMAIL_TRIGGERS.map(trigger => {
                const inst = form.email_instellingen?.[trigger.key] || {};
                const isActive = !!inst.actief;
                const rollen = inst.rollen || [];
                const extra = inst.extra_ontvangers || [];
                return (
                  <div key={trigger.key} className="border border-slate-100 rounded-lg p-3 space-y-2" data-testid={`email-trigger-${trigger.key}`}>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!isActive} onCheckedChange={v => setEmailActief(trigger.key, v)}
                        aria-label={trigger.label} data-testid={`email-${trigger.key}-toggle`} />
                      <div className="flex-1">
                        <span className="text-sm font-medium">{trigger.label}</span>
                        <p className="text-xs text-slate-400">{trigger.desc}</p>
                      </div>
                    </div>
                    {isActive && (
                      <div className="pl-10 space-y-2">
                        {trigger.hasSchedule && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs">Dag</Label>
                              <Select value={inst?.dag || undefined} onValueChange={v => setEmailInst(trigger.key, 'dag', v)}>
                                <SelectTrigger className="h-7 text-xs mt-1" aria-label={`Dag ${trigger.label}`} data-testid={`email-${trigger.key}-dag`}><SelectValue placeholder="Kies een dag" /></SelectTrigger>
                                <SelectContent>
                                  {DAGEN_OPTIES.filter(o => o.value !== 'geen').map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Tijd</Label>
                              <Input type="time" value={inst?.tijd || ''} onChange={e => setEmailInst(trigger.key, 'tijd', e.target.value)}
                                aria-label={`Tijd ${trigger.label}`}
                                className="h-7 text-xs mt-1" data-testid={`email-${trigger.key}-tijd`} />
                            </div>
                          </div>
                        )}
                        <div>
                          <Label className="text-xs">Ontvangers (rollen)</Label>
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {ROLLEN.map(rol => (
                              <label key={rol} className="flex items-center gap-1 text-xs cursor-pointer bg-slate-50 px-2 py-1 rounded border">
                                <Checkbox checked={rollen.includes(rol)} onCheckedChange={() => toggleEmailRol(trigger.key, rol)} className="h-3.5 w-3.5" />
                                {rol}
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Extra ontvangers (komma-gescheiden)</Label>
                          <ExtraOntvangers waarde={extra} onOpslaan={val => setEmailExtra(trigger.key, val)}
                            testId={`email-extra-${trigger.key}`} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Info-blokken (template-niveau) */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="info-blokken-section">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#1f2548] flex items-center gap-2"><Info className="w-4 h-4" /> Info-blokken</h3>
              <Button size="sm" variant="outline" onClick={addInfoBlok} data-testid="add-info-blok-btn"><Plus className="w-3 h-3 mr-1" /> Info-blok</Button>
            </div>
            {form.info_blokken.map((ib, i) => (
              <div key={i} className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-2" data-testid={`info-blok-${i}`}>
                <div className="flex items-center gap-2">
                  <button onClick={() => moveInfoBlok(i, -1)} disabled={i === 0} className="p-0.5 hover:bg-amber-100 rounded disabled:opacity-30"><ChevronUp className="w-3 h-3" /></button>
                  <button onClick={() => moveInfoBlok(i, 1)} disabled={i === form.info_blokken.length - 1} className="p-0.5 hover:bg-amber-100 rounded disabled:opacity-30"><ChevronDown className="w-3 h-3" /></button>
                  <Input value={ib.titel} onChange={e => updateInfoBlok(i, 'titel', e.target.value)} placeholder="Titel" className="flex-1 h-8 text-sm" />
                  <button onClick={() => removeInfoBlok(i)} className="p-1 hover:bg-red-50 rounded text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
                <Textarea value={ib.tekst} onChange={e => updateInfoBlok(i, 'tekst', e.target.value)} placeholder="Informatietekst..." rows={2} className="text-sm" />
              </div>
            ))}
          </div>

          {/* Secties builder */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="secties-section">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#1f2548]">Secties</h3>
              <Button size="sm" variant="outline" onClick={addSectie} data-testid="add-sectie-btn"><Plus className="w-3 h-3 mr-1" /> Sectie</Button>
            </div>
            {form.secties.map((sectie, si) => (
              <div key={si} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`sectie-editor-${si}`}>
                <div className="bg-slate-50 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
                  <button onClick={() => moveSectie(si, -1)} disabled={si === 0} className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveSectie(si, 1)} disabled={si === form.secties.length - 1} className="p-0.5 hover:bg-slate-200 rounded disabled:opacity-30"><ChevronDown className="w-4 h-4" /></button>
                  <Input value={sectie.naam} onChange={e => updateSectie(si, 'naam', e.target.value)} placeholder="Sectie naam" className="flex-1 h-8 text-sm font-medium" data-testid={`sectie-naam-${si}`} />
                  <Badge variant="secondary" className="text-xs">{sectie.items?.length || 0} items</Badge>
                  <button onClick={() => removeSectie(si)} className="p-1 hover:bg-red-50 rounded text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="p-3 space-y-2">
                  {/* Sectie info-blokken */}
                  {(sectie.info_blokken || []).map((ib, ibi) => (
                    <div key={ibi} className="border border-amber-200 bg-amber-50/50 rounded p-2 space-y-1">
                      <div className="flex items-center gap-2">
                        <Input value={ib.titel} onChange={e => updateSectieInfoBlok(si, ibi, 'titel', e.target.value)} placeholder="Info titel" className="flex-1 h-7 text-xs" />
                        <button onClick={() => removeSectieInfoBlok(si, ibi)} className="p-0.5 hover:bg-red-50 rounded text-red-400"><Trash2 className="w-3 h-3" /></button>
                      </div>
                      <Textarea value={ib.tekst} onChange={e => updateSectieInfoBlok(si, ibi, 'tekst', e.target.value)} placeholder="Info tekst" rows={1} className="text-xs" />
                    </div>
                  ))}
                  {/* Items */}
                  {sectie.items?.map((item, ii) => (
                    <ItemRow key={ii} item={item} index={ii} total={sectie.items.length}
                      onUpdate={(k, v) => updateItemInSectie(si, ii, k, v)}
                      onMerge={(patch) => mergeItemInSectie(si, ii, patch)}
                      onDelete={() => removeItemFromSectie(si, ii)}
                      onMove={(dir) => moveItemInSectie(si, ii, dir)}
                    />
                  ))}
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" variant="outline" onClick={() => addItemToSectie(si)} className="text-xs" data-testid={`add-item-sectie-${si}`}>
                      <Plus className="w-3 h-3 mr-1" /> Item
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => addSectieInfoBlok(si)} className="text-xs text-amber-600">
                      <Info className="w-3 h-3 mr-1" /> Info-blok
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Items zonder sectie */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="items-zonder-sectie-section">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-[#1f2548]">Items zonder sectie</h3>
              <Button size="sm" variant="outline" onClick={addLooseItem} data-testid="add-los-item-btn"><Plus className="w-3 h-3 mr-1" /> Item</Button>
            </div>
            {form.items_zonder_sectie.map((item, i) => (
              <ItemRow key={i} item={item} index={i} total={form.items_zonder_sectie.length}
                onUpdate={(k, v) => updateLooseItem(i, k, v)}
                onMerge={(patch) => mergeLooseItem(i, patch)}
                onDelete={() => removeLooseItem(i)}
                onMove={(dir) => moveLooseItem(i, dir)}
              />
            ))}
          </div>
        </div>

        {/* ── Right: Live Preview ── */}
        <div className="lg:col-span-2 lg:sticky lg:top-4 lg:self-start">
          <LivePreview form={form} />
        </div>
      </div>
    </div>
  );
};

// ── Template List View ─────────────────────────────────────────────────────

const TemplateListView = ({ templates, onEdit, onNew, onDelete, onToggleActief, onTogglePauze, onSoftDelete, loading }) => {
  const freqLabel = (f) => FREQ_OPTIONS.find(o => o.value === f?.type)?.label || f?.type || '-';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-4" data-testid="template-list">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="templates-v2-title">
            <ClipboardList className="w-6 h-6" /> Checklist Templates
          </h1>
          <p className="text-slate-500 text-sm">Beheer checklist templates (V2)</p>
        </div>
        <Button onClick={onNew} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="new-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Nieuw template
        </Button>
      </div>

      <div className="space-y-3">
        {templates.map(tmpl => {
          const itemCount = (tmpl.secties || []).reduce((s, sec) => s + (sec.items?.length || 0), 0) + (tmpl.items_zonder_sectie?.length || 0);
          const isInactief = tmpl.actief === false;
          return (
            <div key={tmpl.id} className={`bg-white rounded-xl border p-5 hover:shadow-md transition-all cursor-pointer group ${isInactief ? 'border-slate-300 opacity-60' : 'border-slate-200'}`}
              onClick={() => onEdit(tmpl)} data-testid={`template-card-${tmpl.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className={`font-semibold ${isInactief ? 'text-slate-400' : 'text-slate-800'}`}>{tmpl.naam}</h3>
                    {isInactief && <Badge className="bg-slate-200 text-slate-500" data-testid={`inactief-badge-${tmpl.id}`}>Inactief</Badge>}
                    {!isInactief && tmpl.gepauzeerd && <Badge className="bg-amber-100 text-amber-700" data-testid={`gepauzeerd-badge-${tmpl.id}`}>Gepauzeerd</Badge>}
                    <Badge className={tmpl.type === 'locatie' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                      {tmpl.type === 'locatie' ? <><Globe className="w-3 h-3 mr-1" />Locatie</> : <><MapPin className="w-3 h-3 mr-1" />Persoonlijk</>}
                    </Badge>
                    <Badge variant="outline" className="text-xs">{freqLabel(tmpl.frequentie)}</Badge>
                    {tmpl.score_berekening && <Badge variant="outline" className="text-xs text-green-600 border-green-300">Score</Badge>}
                  </div>
                  <p className="text-sm text-slate-500">
                    {tmpl.secties?.length || 0} secties, {itemCount} items
                    {tmpl.toewijzing?.alle_locaties ? ' — Alle locaties' : ` — ${tmpl.toewijzing?.locatie_ids?.length || 0} locaties`}
                  </p>
                  {tmpl.beschrijving && <p className="text-xs text-slate-400 mt-1 truncate">{tmpl.beschrijving}</p>}
                </div>
                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <Switch checked={!tmpl.gepauzeerd}
                      onCheckedChange={(v) => onTogglePauze(tmpl.id, !v)}
                      data-testid={`pauze-toggle-${tmpl.id}`} />
                    <span className={`text-xs ${tmpl.gepauzeerd ? 'text-amber-600 font-medium' : 'text-slate-500'}`}>{tmpl.gepauzeerd ? 'Pauze' : 'Live'}</span>
                  </div>
                  <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                    <Switch checked={tmpl.actief !== false}
                      onCheckedChange={(v) => onToggleActief(tmpl.id, v)}
                      data-testid={`actief-toggle-${tmpl.id}`} />
                    <span className="text-xs text-slate-500">{tmpl.actief !== false ? 'Actief' : 'Uit'}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onEdit(tmpl); }} data-testid={`edit-btn-${tmpl.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 hover:bg-red-50"
                    onClick={(e) => { e.stopPropagation(); onSoftDelete(tmpl); }} data-testid={`delete-btn-${tmpl.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
        {templates.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 mb-3">Geen templates gevonden</p>
            <Button onClick={onNew} variant="outline">Maak je eerste template</Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────

const ChecklistTemplatesV2 = () => {
  const [view, setView] = useState('list');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locaties, setLocaties] = useState([]);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [softDeleting, setSoftDeleting] = useState(null);
  const [genereerModal, setGenereerModal] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_V2}/checklist-templates`, { headers: hdrs() });
      if (res.ok) setTemplates(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const fetchLocaties = useCallback(async () => {
    try {
      const res = await fetch(`${API}/locaties`, { headers: hdrs() });
      if (res.ok) setLocaties(await res.json());
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchTemplates(); fetchLocaties(); }, [fetchTemplates, fetchLocaties]);

  // 6.27e/E14g: ?template=<id>#email opent direct de editor van die template en
  // scrollt naar de e-mailsectie (link uit de matrix op /beheer/email).
  const [zoekParams, setZoekParams] = useSearchParams();
  useEffect(() => {
    const id = zoekParams.get('template');
    if (!id || !templates.length) return;
    const tmpl = templates.find(t => String(t.id || t._id) === String(id));
    if (!tmpl) return;
    setEditing(tmpl);
    setView('editor');
    setZoekParams(params => { params.delete('template'); return params; }, { replace: true });
    setTimeout(() => {
      document.querySelector('[data-testid="email-instellingen-section"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  }, [zoekParams, templates, setZoekParams]);

  const handleEdit = (tmpl) => { setEditing(tmpl); setView('editor'); };
  const handleNew = () => { setEditing(null); setView('editor'); };
  const handleBack = () => { setView('list'); setEditing(null); fetchTemplates(); };
  const handleDelete = async () => {
    if (!deleting) return;
    try {
      const res = await fetch(`${API_V2}/checklist-templates/${deleting.id}`, { method: 'DELETE', headers: hdrs() });
      if (res.ok) { toast.success('Template gedeactiveerd'); fetchTemplates(); }
      else toast.error('Verwijderen mislukt');
    } catch { toast.error('Verwijderen mislukt'); }
    finally { setDeleting(null); }
  };

  const handleToggleActief = async (templateId, actief) => {
    try {
      const res = await fetch(`${API_V2}/checklist-templates/${templateId}/toggle-actief`, {
        method: 'PATCH', headers: hdrs(),
        body: JSON.stringify({ actief }),
      });
      if (res.ok) {
        toast.success(actief ? 'Template geactiveerd' : 'Template gedeactiveerd');
        fetchTemplates();
      } else toast.error('Status wijzigen mislukt');
    } catch { toast.error('Status wijzigen mislukt'); }
  };

  const handleTogglePauze = async (templateId, gepauzeerd) => {
    try {
      const res = await fetch(`${API_V2}/checklist-templates/${templateId}/toggle-gepauzeerd`, {
        method: 'PATCH', headers: hdrs(),
        body: JSON.stringify({ gepauzeerd }),
      });
      if (res.ok) {
        toast.success(gepauzeerd ? 'Template gepauzeerd' : 'Template is nu live');
        fetchTemplates();
      } else toast.error('Pauze wijzigen mislukt');
    } catch { toast.error('Pauze wijzigen mislukt'); }
  };

  const handleOpenGenereer = (tmpl) => {
    setGenereerModal({ id: tmpl.id, naam: tmpl.naam });
  };

  const handleSoftDelete = (tmpl) => {
    setSoftDeleting(tmpl);
  };

  const handleSoftDeleted = () => {
    // If we were in editor, go back to list
    if (view === 'editor') {
      setView('list');
      setEditing(null);
    }
    fetchTemplates();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-0" data-testid="checklist-templates-v2-page">
      {view === 'list' && (
        <TemplateListView templates={templates} onEdit={handleEdit} onNew={handleNew}
          onDelete={setDeleting} onToggleActief={handleToggleActief}
          onTogglePauze={handleTogglePauze}
          onSoftDelete={handleSoftDelete} loading={loading} />
      )}
      {view === 'editor' && (
        <TemplateEditor template={editing} onBack={handleBack} onSaved={fetchTemplates} locaties={locaties}
          onGenerate={() => editing?.id && handleOpenGenereer(editing)}
          onSoftDelete={(tmpl) => handleSoftDelete(tmpl)} />
      )}

      {/* Genereer interstitial modal */}
      <GenereerModal
        open={!!genereerModal}
        onClose={() => setGenereerModal(null)}
        templateId={genereerModal?.id}
        templateNaam={genereerModal?.naam}
      />

      {/* Soft Delete modal */}
      <SoftDeleteModal
        open={!!softDeleting}
        onClose={() => setSoftDeleting(null)}
        template={softDeleting}
        onDeleted={handleSoftDeleted}
      />

      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Template deactiveren?</DialogTitle>
            <DialogDescription>"{deleting?.naam}" wordt gedeactiveerd en niet meer zichtbaar.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Annuleren</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-btn">Deactiveren</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChecklistTemplatesV2;
